import { useState, useEffect, useMemo, useCallback } from "react";
import useDropzoneUpload from "./DropzoneUpload.hook";
import { useUploadDirectToStorage } from "src/queries/hooks/storage";
import { selectedAccountIdSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { uploadDirectFilesToStorage, prepareBasicFileData } from "../FileUpload.utils";
import { ERROR_MESSAGES, ERROR_TYPE, PROGRESS_MODE, DEFAULT_ACCEPTED_TYPES, PROGRESS } from "../FileUpload.consts";

const useFileUploadDialog = ({
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  onSubmit,
  onClose,
  multiple = true,
  fileToReplace = null,
  preUploadValidation
} = {}) => {
  const [errors, setErrors] = useState([]);
  const [duplicateFileError, setDuplicateFileError] = useState(null);
  const [hover, setHover] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const accountId = useAppStore(selectedAccountIdSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const uploadDirectToStorage = useUploadDirectToStorage(accountId, selectedAssistant?.assistantId);

  useEffect(() => {
    let timeoutId;
    if (duplicateFileError) {
      timeoutId = setTimeout(() => {
        setDuplicateFileError(null);
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [duplicateFileError]);

  const handleDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    if (preUploadValidation) {
      const files = acceptedFiles.map(prepareBasicFileData);
      const error = preUploadValidation(files);
      if (error) {
        if (error === ERROR_TYPE.DUPLICATE_FILE) {
          setDuplicateFileError(ERROR_MESSAGES.DUPLICATE_FILE(files));
        }
        return;
      }
    }
    setIsUploading(true);

    // Clear duplicate file error on new drop
    setDuplicateFileError(null);

    try {
      const basicFiles = acceptedFiles.map(prepareBasicFileData);

      // Check for duplicate files
      const duplicateFiles = [];
      const uniqueFiles = [];

      basicFiles.forEach(newFile => {
        const isDuplicate = uploadedFiles.some(
          existingFile =>
            (existingFile.file_name || existingFile.name) === newFile.file_name
        ) || (fileToReplace && `${fileToReplace.file_name}.${fileToReplace.file_type}` === newFile.file_name);

        if (isDuplicate) {
          duplicateFiles.push(newFile);
        } else {
          uniqueFiles.push(newFile);
        }
      });

      if (duplicateFiles.length > 0) {
        setDuplicateFileError(ERROR_MESSAGES.DUPLICATE_FILE(duplicateFiles));

        if (uniqueFiles.length === 0 || !multiple) {
          setIsUploading(false);
          return;
        }
      }

      if (!multiple) {
        setUploadedFiles(uniqueFiles);
      } else {
        setUploadedFiles(prev => [...prev, ...uniqueFiles]);
      }
      const filesToUpload = acceptedFiles.filter(file =>
        uniqueFiles.some(uniqueFile => uniqueFile.file_name === prepareBasicFileData(file).file_name)
      );
      const result = await uploadDirectFilesToStorage({
        files: filesToUpload,
        uploadDirectToStorage,
        setFileProgress,
        setErrors,
        progressMode: !multiple ? PROGRESS_MODE.REPLACE : PROGRESS_MODE.MERGE
      });

      const updatedFiles = uniqueFiles.map((file, index) => ({
        ...file,
        path: result?.[index]?.path || null,
        error: result?.[index]?.error || null
      }));

      if (!multiple) {
        setUploadedFiles([updatedFiles[0]]);
        if (updatedFiles[0]?.error) {
          setErrors([{
            file_name: updatedFiles[0].file_name,
            error: updatedFiles[0].error || ERROR_MESSAGES.UPLOAD_FAILED
          }]);
        }
      } else {
        setUploadedFiles(prev => {
          const filteredPrev = prev.filter(
            prevFile => !uniqueFiles.some(newFile =>
              newFile.file_name === (prevFile.file_name || prevFile.name)
            )
          );
          return [...filteredPrev, ...updatedFiles];
        });

        const newErrors = updatedFiles
          .filter(file => file.error)
          .map(file => ({
            fileName: file.file_name,
            error: file.error || ERROR_MESSAGES.UPLOAD_FAILED
          }));

        if (newErrors.length > 0) {
          setErrors(prev => [...prev, ...newErrors]);
        }
      }
    } catch (error) {
      const errorEntries = !multiple
        ? [{ file_name: acceptedFiles[0].name, error: ERROR_MESSAGES.UPLOAD_FAILED }]
        : acceptedFiles.map(file => ({ file_name: file.name, error: ERROR_MESSAGES.UPLOAD_FAILED }));

      setErrors(prev => [...prev, ...errorEntries]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDropRejected = (rejectedFiles) => {
    rejectedFiles.forEach(rejection => {
      const { file } = rejection;
      const normalizedFile = prepareBasicFileData(file);
      const errorMessage = rejection.errors[0].code === ERROR_TYPE.INVALID_TYPE
        ? ERROR_MESSAGES.UNSUPPORTED_FORMAT(acceptedFileTypes)
        : ERROR_MESSAGES.UPLOAD_FAILED;

      if (!multiple) {
        setErrors([{ file_name: normalizedFile.file_name, error: errorMessage }]);
        setUploadedFiles([normalizedFile]);
      } else {
        setErrors(prev => [...prev, { file_name: normalizedFile.file_name, error: errorMessage }]);
        setUploadedFiles(prev => [...prev, normalizedFile]);
      }
    });
  };

  const removeFile = (fileToRemove) => {
    const fileName = fileToRemove.file_name || fileToRemove.name;

    if (fileToReplace && fileToRemove === fileToReplace) {
      setErrors(prev => prev.filter(err => err.file_name !== fileName));
      return;
    }

    if (!multiple) {
      setUploadedFiles([]);
      setFileProgress({});
      setErrors([]);
    } else {
      setUploadedFiles(prev => prev.filter(file => file !== fileToRemove));

      setFileProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileName];
        return newProgress;
      });

      setErrors(prev => prev.filter(err => err.file_name !== fileName));
    }

    setDuplicateFileError(null);
  };

  const getFileError = useCallback((file) => {
    const fileName = file.file_name;
    const fileError = errors.find(err => err.file_name === fileName);
    return fileError ? fileError.error : null;
  }, [errors]);

  const handleClose = () => {
    setErrors([]);
    setDuplicateFileError(null);
    setUploadedFiles([]);
    if (onClose) onClose();
  };

  const handleSubmit = (files) => {
    if (onSubmit) {
      if (!files || files.length === 0) {
        if (fileToReplace) {
          onSubmit([fileToReplace]);
        } else {
          onSubmit(null);
        }
        return;
      }

      onSubmit(files);
      handleClose();
    }
  };

  const dropzone = useDropzoneUpload({
    acceptedFileTypes,
    multiple,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected
  });

  const hasFiles = uploadedFiles.length > 0 || (fileToReplace !== null && uploadedFiles.length === 0);

  const uploadedCount = uploadedFiles.filter(file => {
    const fileName = file.file_name || file.name;
    return fileProgress[fileName] === PROGRESS.ENDED && !getFileError(file);
  }).length;

  const allFilesUploaded = useMemo(() => {
    if (!hasFiles) return false;

    return uploadedFiles.every(file => {
      const fileName = file.file_name || file.name;
      return fileProgress[fileName] === PROGRESS.ENDED && !getFileError(file);
    });
  }, [uploadedFiles, fileProgress, hasFiles, getFileError]);

  const filesToDisplay = useMemo(() => {
    if (fileToReplace && uploadedFiles.length === 0) {
      return [fileToReplace];
    }
    return uploadedFiles;
  }, [uploadedFiles, fileToReplace]);

  return {
    hover,
    setHover,
    uploadedFiles,
    fileProgress,
    isUploading,
    removeFile,
    getFileError,
    handleClose,
    handleSubmit,
    errors,
    duplicateFileError,
    hasFiles,
    uploadedCount,
    allFilesUploaded,
    filesToDisplay,
    ...dropzone
  };
};

export default useFileUploadDialog;
